import { savePolicy, validatePolicyContacts } from "@/lib/catalog-api";
import { useConfiguration } from "./useConfiguration";
import { toPolicyCode } from "../lib/drafts";
import { saveErrorMessage } from "../lib/saveMessage";
import { useToast } from "./useToast";

/**
 * Coverage path, staff, and contacts are one draft.
 * Save sends that draft in a single policy API call.
 * Request-type assignment is saved only from the Requests step.
 */
export function useSaveCoveragePath() {
  const { policyDraft, setSaving, commitSavedPolicy } = useConfiguration();
  const toast = useToast();

  const save = async () => {
    const displayName = policyDraft.displayName.trim();
    const code = (
      policyDraft.code.trim() || toPolicyCode(displayName)
    ).toUpperCase();
    if (!displayName || !code) {
      toast.error("Name is required");
      return;
    }

    const contactsError = validatePolicyContacts(policyDraft.tiers);
    if (contactsError) {
      toast.error(contactsError);
      return;
    }

    setSaving(true);
    try {
      const result = await savePolicy(
        {
          code,
          displayName,
          isActive: policyDraft.isActive,
          tiers: policyDraft.tiers.map((tier) => ({
            escalationTierId: tier.escalationTierId || null,
            tierOrder: tier.tierOrder,
            roleCode: tier.roleCode.trim().toUpperCase(),
            timeoutSeconds: tier.timeoutSeconds,
            displayName: tier.displayName.trim(),
            channels: tier.channels.map((channel, index) => ({
              escalationTierChannelId: channel.escalationTierChannelId || null,
              notificationChannelId: channel.notificationChannelId,
              sortOrder: channel.sortOrder || index + 1,
              isRequired: channel.isRequired,
              templateKey: channel.templateKey ?? null,
              destinationRuleJson: channel.destinationRuleJson ?? null,
            })),
          })),
        },
        policyDraft.escalationPolicyId,
      );
      if (!result.ok || !result.policy) {
        toast.error(saveErrorMessage(result.message));
        return;
      }
      commitSavedPolicy(result.policy);
      toast.success("Coverage path saved.");
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return { save };
}
